import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useStyles } from "./EmailContent.style";
import { Data, UserData } from "../../../types/org.type";
import axios from "axios";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import { Button } from "@funded-here-interface/common";
import {
  Microservice,
  getBaseUrl,
} from "@funded-here-interface/common/src/constant/baseUrl";

interface EmailContentProps {
  companyName: string;
  userData: UserData | undefined;
  data: Data | undefined;
}

const EmailContent: React.FC<EmailContentProps> = ({
  userData,
  companyName,
  data,
}) => {
  const { classes } = useStyles();
  const [emailContent, setEmailContent] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const userEmail = userData?.email || "-";
  const userName = userData?.firstName + " " + userData?.lastName;
  const entityType = userData?.entityType;
  const userId = userData?.id || null;
  const isIndividual = entityType === "individual";
  const isIspv = userData?.pendingOnBoardingAs === "ispv";
  const isInvestor = userData?.pendingOnBoardingAs === "investor";
  const isDistributor = userData?.pendingOnBoardingAs === "distributor";
  const isKYCStatusPending = data?.kycStatus === KYCStatus.PENDING_APPROVAL;

  // Helper function to capitalize the first letter of each word
  const capitalizeWords = (str: string | undefined): string => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Determine receiver name based on entityType
  const receiverName = isIndividual ? capitalizeWords(userName) : companyName;

  const handleSendClick = () => {
    if (isKYCStatusPending) {
      setShowPopup(true);
    }
  };

  const handleConfirmSend = async () => {
    setShowPopup(false);
    setEmailContent("");
    try {
      await axios.post(
        `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/organization/send-resubmission-email`,
        {
          userId: userId,
          name: receiverName,
          email: userEmail,
          content: emailContent,
          authRepOrgRoleId: userData?.orgRole?.[0]?.id ?? null,
          isIspv: isIspv,
          isInvestor: isInvestor,
          isDistributor: isDistributor,
        },
      );
      toast.success("Email sent successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to send email. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleCancel = () => {
    setShowPopup(false);
  };

  const popupContent = showPopup ? (
    <div className={classes.popupOverlay}>
      <div className={classes.popupContainer}>
        <p className={classes.popupTitle}>Confirm Email Sending</p>
        <p className={classes.popupDescription}>
          By clicking "Confirm", an email containing your entered remarks will
          be sent to the following recipient to request resubmission of
          onboarding details.
        </p>
        <p className={classes.popupText}>
          <strong>Recipient Name:</strong> {receiverName || "Not provided"}
        </p>
        <p className={classes.popupText}>
          <strong>Recipient Email:</strong> {userEmail || "Not provided"}
        </p>
        <p className={classes.popupText}>
          <strong>Remarks for Onboarding Details Resubmission:</strong>{" "}
          {emailContent || "Not provided"}
        </p>
        <div className={classes.popupButtonContainer}>
          <button className={classes.cancelButton} onClick={handleCancel}>
            Cancel
          </button>
          <button className={classes.confirmButton} onClick={handleConfirmSend}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const toastContainer = (
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
      style={{ zIndex: 10000 }}
    />
  );

  return (
    <div className={classes.container}>
      <div className={classes.formContainer}>
        <label className={classes.label}>Recipient Name</label>
        <input
          type="text"
          value={receiverName || ""}
          placeholder="Recipient name"
          readOnly
          className={classes.input}
        />
        <label className={classes.label}>Recipient Email</label>
        <input
          type="email"
          value={userEmail}
          placeholder="Enter recipient email"
          readOnly
          className={classes.input}
        />
        <label className={classes.label}>
          Remarks for Onboarding Details Resubmission
        </label>
        <textarea
          value={emailContent}
          onChange={(e) => setEmailContent(e.target.value)}
          placeholder="Enter remarks for onboarding details resubmission"
          rows={4}
          className={classes.textarea}
        />
        <div className={classes.buttonContainer}>
          <Button
            onClick={handleSendClick}
            disabled={!isKYCStatusPending || !emailContent.trim()}
            textColor="white"
            backgroundColor="rgb(55, 134, 214)"
          >
            Send
          </Button>
        </div>
      </div>

      {showPopup && ReactDOM.createPortal(popupContent, document.body)}
      {ReactDOM.createPortal(toastContainer, document.body)}
    </div>
  );
};

export default EmailContent;
